/**
 * Scenario content carries free-form English tags (`tags: ["vishing", "BEC", …]`) that badges and
 * authoring tools match on. The catalogue needs something else: a short, translated threat class a
 * facilitator can scan. This maps the authoring vocabulary onto a small, localisable set and drops
 * anything it does not recognise, so a new tag never leaks untranslated English into the UI.
 */

/** Canonical threat classes, in the order they should appear on a card. */
const ORDER = [
  "supply-chain",
  "vishing",
  "deepfake",
  "bec",
  "payment-fraud",
  "ransomware",
  "extortion",
  "third-party",
  "saas",
  "identity",
  "help-desk",
  "remote-access",
  "data-breach",
  "impostor",
  "tech-support",
  "family-emergency",
  "account-takeover",
  "phishing",
  "money-mule",
] as const;

export type ThreatClass = (typeof ORDER)[number];

const BY_TAG: Record<string, ThreatClass> = {
  vishing: "vishing",
  deepfake: "deepfake",
  bec: "bec",
  "payment fraud": "payment-fraud",
  "funds transfer fraud": "payment-fraud",
  ransomware: "ransomware",
  "double extortion": "extortion",
  extortion: "extortion",
  "third-party": "third-party",
  "vendor management": "third-party",
  supplier: "third-party",
  "supply chain": "supply-chain",
  npm: "supply-chain",
  "open source": "supply-chain",
  dependency: "supply-chain",
  saas: "saas",
  oauth: "saas",
  identity: "identity",
  "mfa reset": "identity",
  mfa: "identity",
  "help desk": "help-desk",
  "remote access": "remote-access",
  "data breach": "data-breach",
  "impostor scam": "impostor",
  "bank impersonation": "impostor",
  "government impersonation": "impostor",
  "phantom hacker": "impostor",
  "tech support scam": "tech-support",
  "grandparent scam": "family-emergency",
  "family emergency": "family-emergency",
  "voice clone": "deepfake",
  "account takeover": "account-takeover",
  gaming: "account-takeover",
  phishing: "phishing",
  "money mule": "money-mule",
};

const RANK = new Map<ThreatClass, number>(ORDER.map((c, i) => [c, i]));

/**
 * The threat classes for a scenario, deduplicated and in canonical order.
 * `limit` caps how many a card shows; the rest stay available to callers that want them all.
 */
export function threatClasses(tags: readonly string[], limit = Number.POSITIVE_INFINITY): ThreatClass[] {
  const seen = new Set<ThreatClass>();
  for (const tag of tags) {
    const cls = BY_TAG[tag.trim().toLowerCase()];
    if (cls) seen.add(cls);
  }
  return [...seen].sort((a, b) => (RANK.get(a) ?? 0) - (RANK.get(b) ?? 0)).slice(0, limit);
}
